import { CallState, LoadingState } from '@lcs/shared/api';
import { createReducer, on } from '@ngrx/store';
import { Tiles, TileTypes, TileViewModes } from 'generated/graphql-types';
import { onReset } from 'src/app/authentication/reset-reducer';
import { getTileViewMode } from '../../tiles/models/tile-view-mode.model';
import * as TileActions from '../../tiles/state/tile.actions';
import { loadPreviewTile } from '../add-dashboard-tile/state/add-dashboard-tile.actions';
import { getPreservedLayoutFromDashboard, mapLayoutToTargetColumns } from '../models/dashboard-layout-utils';
import { DashboardTile, FullscreenDashboardTile, PreviewDashboardTile } from '../models/dashboard-tile.model';
import { Dashboard } from '../models/dashboard.model';
import { SaveDashboardTileLayoutConfigurations_saveDashboardTileLayoutConfigurations } from '../queries/generated/SaveDashboardTileLayoutConfigurations';
import { DashboardGridLayoutItem } from '../views/dashboard-view.component';
import * as DashboardActions from './dashboard.actions';

export const dashboardFeatureKey = 'dashboard';

const defaultDashboard = {
   dashboardId: 0,
   dashboardTiles: [
      {
         dashboardTileId: 0,
         dashboardTileLayoutConfiguration: {
            color: '#000000',
            dashboardTileLayoutConfigurationId: 0,
            height: 1,
            name: 'Clock',
            width: 1,
            maxHeight: null,
            maxWidth: null,
            minHeight: null,
            minWidth: null,
            refreshTimeSeconds: null,
            x: 0,
            y: 0,
         },
         dashboardTileParameters: [],
         tile: {
            tileId: Tiles.Clock,
            description: 'Clock',
            name: 'Clock',
            tileViewModeBound: {
               heightLowerBound: 5,
               heightUpperBound: 10,
               widthLowerBound: 5,
               widthUpperBound: 10,
            },
            canEditRefreshTime: false,
            designerDescription: 'Clock',
            isCustom: false,
            typeId: TileTypes.Clock,
         },
         totalMillisecondsProcessed: 0,
         totalTimesProcessed: 0,
         viewMode: TileViewModes.Small,
      },
      {
         dashboardTileId: 1,
         dashboardTileLayoutConfiguration: {
            color: '#000000',
            dashboardTileLayoutConfigurationId: 1,
            height: 1,
            name: 'Parked Calls',
            width: 4,
            maxHeight: 3,
            maxWidth: 2,
            minHeight: null,
            minWidth: null,
            refreshTimeSeconds: null,
            x: 1,
            y: 1,
         },
         dashboardTileParameters: [],
         tile: {
            tileId: Tiles.CallParking,
            description: 'Parked Calls',
            name: 'Parked Calls',
            tileViewModeBound: {
               heightLowerBound: 1,
               heightUpperBound: 3,
               widthLowerBound: 1,
               widthUpperBound: 2,
            },
            canEditRefreshTime: false,
            designerDescription: 'Parked Calls',
            isCustom: false,
            typeId: TileTypes.Table,
         },
         totalMillisecondsProcessed: 0,
         totalTimesProcessed: 0,
         viewMode: TileViewModes.Small,
      },
   ],
   dashboardUserId: 0,
   lastSavedColumnLayoutSize: 1,
   name: 'Default Dashboard',
};

export interface State {
   callState: CallState;
   dashboards: Dashboard[] | null;
   selectedDashboard: Dashboard | null;
   currentDashboardColumnLayoutSize: number;
   displayLayout: DashboardGridLayoutItem[];
   preservedLayout: DashboardGridLayoutItem[];
   fullscreenTile: FullscreenDashboardTile | null;
   previewTile: PreviewDashboardTile | null;
}

export const initialState: State = {
   callState: LoadingState.Init,
   dashboards: null,
   selectedDashboard: null,
   currentDashboardColumnLayoutSize: 6,
   displayLayout: [],
   preservedLayout: [],
   fullscreenTile: null,
   previewTile: null,
};

export const reducer = createReducer(
   initialState,
   onReset(initialState),
   on(DashboardActions.pageEntered, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.pageLoadedSuccess, (state, action) => {
      return {
         ...state,
         dashboards: action.dashboards,
         selectedDashboard: action.selectedDashboard,
         preservedLayout: getPreservedLayoutFromDashboard(action.selectedDashboard),
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.pageLoadedFailure, (state, action) => {
      return {
         ...state,
         callState: { error: action.error },
      };
   }),
   on(DashboardActions.closeFullscreenTile, (state, action) => {
      return {
         ...state,
         fullscreenTile: null,
      };
   }),
   on(DashboardActions.saveGridLayoutChange, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.openFullscreenTile, (state, action) => {
      return {
         ...state,
         fullscreenTile: action.config,
      };
   }),
   on(DashboardActions.addDashboard, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.setCurrentDashboardColumnLayoutSize, (state, action) => {
      return {
         ...state,
         currentDashboardColumnLayoutSize: action.currentDashboardColumnLayoutSize,
      };
   }),
   on(DashboardActions.addDashboardSuccess, (state, action) => {
      return {
         ...state,
         selectedDashboard: action.dashboard,
         preservedLayout: getPreservedLayoutFromDashboard(action.dashboard),
         dashboards: state.dashboards ? [...state.dashboards, action.dashboard] : [action.dashboard],
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.addDashboardFailure, (state, action) => {
      return {
         ...state,
         callState: { error: action.error },
      };
   }),
   on(DashboardActions.deleteDashboard, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.deleteDashboardSuccess, (state, action) => {
      const dashboards = (state.dashboards ?? []).filter((d) => d.dashboardId !== action.dashboardId);
      const updatedSelectedDashboard = dashboards ? dashboards[0] : null;
      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard,
         preservedLayout: getPreservedLayoutFromDashboard(updatedSelectedDashboard),
         dashboards,
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.deleteDashboardFailure, (state, action) => {
      return {
         ...state,
         callState: { error: action.error },
      };
   }),
   on(DashboardActions.reloadSelectedDashboard, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.reloadSelectedDashboardSuccess, (state, action) => {
      return {
         ...state,
         selectedDashboard: action.dashboard,
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(action.dashboard, state.dashboards),
         preservedLayout: getPreservedLayoutFromDashboard(action.dashboard),
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.renameDashboard, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(DashboardActions.renameDashboardSuccess, (state, action) => {
      const updatedSelectedDashboard: Dashboard | null = state.selectedDashboard
         ? { ...state.selectedDashboard, name: action.dashboard.name }
         : null;
      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard,
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(updatedSelectedDashboard, state.dashboards),
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.renameDashboardFailure, (state, action) => {
      return {
         ...state,
         callState: { error: action },
      };
   }),
   on(DashboardActions.saveGridLayoutChangeSuccess, (state, action) => {
      const updatedSelectedDashboard: Dashboard | null = getUpdatedSelectedDashboardFromGridLayoutSaveSuccess(
         state.selectedDashboard,
         action.dirtyDashboardTileLayoutConfigurations,
         action.columnLayoutSize
      );
      const preservedLayout = getPreservedLayoutFromDashboard(updatedSelectedDashboard);

      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard ? { ...updatedSelectedDashboard } : null,
         preservedLayout,
         displayLayout: mapLayoutToTargetColumns(
            updatedSelectedDashboard?.lastSavedColumnLayoutSize ?? 1,
            action.columnLayoutSize,
            preservedLayout
         ),
         currentDashboardColumnLayoutSize: action.columnLayoutSize,
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(updatedSelectedDashboard, state.dashboards),
         callState: LoadingState.Loaded,
      };
   }),
   on(DashboardActions.reloadSelectedDashboardFailure, (state, action) => {
      return {
         ...state,
         callState: { error: action.error },
      };
   }),
   on(loadPreviewTile, (state, action) => {
      return {
         ...state,
         previewTile: action.previewTileConfig,
      };
   }),
   on(DashboardActions.updateDashboardTileOnSelectedDashboardByIdSuccess, (state, action) => {
      const updatedSelectedDashboard: Dashboard | null = getUpdatedSelectedWithDashboardTileUpdateSuccess(
         state.selectedDashboard,
         action.dashboardTile
      );
      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard,
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(updatedSelectedDashboard, state.dashboards),
      };
   }),
   on(TileActions.tileSaveSuccess, (state, action) => {
      const updatedSelectedDashboard: Dashboard | null = getUpdatedSelectedDashboardFromTileSaveSuccess(
         state.selectedDashboard,
         action.dashboardTileLayoutConfigurations
      );
      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard,
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(updatedSelectedDashboard, state.dashboards),
      };
   }),
   on(TileActions.deleteDashboardTileSuccess, (state, action) => {
      const updatedSelectedDashboard: Dashboard | null = getUpdatedSelectedDashboardFromTileDeleteSuccess(
         state.selectedDashboard,
         action.dashboardTileId
      );
      return {
         ...state,
         selectedDashboard: updatedSelectedDashboard,
         displayLayout: [...state.displayLayout.filter((l) => l.dashboardTileId !== action.dashboardTileId)],
         preservedLayout: [...state.preservedLayout.filter((l) => l.dashboardTileId !== action.dashboardTileId)],
         dashboards: getUpdatedDashboardsWithNewSelectedDashboard(updatedSelectedDashboard, state.dashboards),
      };
   })
);

function getUpdatedDashboardsWithNewSelectedDashboard(
   updatedSelectedDashboard: Dashboard | null,
   dashboards: Dashboard[] | null
): Dashboard[] | null {
   if (!dashboards || !updatedSelectedDashboard) return null;

   const hasUpdatedSelectedDashboard = dashboards.some((d) => d.dashboardId === updatedSelectedDashboard.dashboardId);

   return hasUpdatedSelectedDashboard
      ? [
           ...dashboards?.map((d) =>
              updatedSelectedDashboard && d.dashboardId === updatedSelectedDashboard.dashboardId
                 ? updatedSelectedDashboard
                 : d
           ),
        ]
      : [...dashboards, { ...updatedSelectedDashboard }];
}
function getUpdatedSelectedDashboardFromTileSaveSuccess(
   dashboard: Dashboard | null,
   dashboardTileLayoutConfigurations: {
      dashboardTileId: number;
      name: string | null;
      color: string | null;
      refreshTimeSeconds: number | null;
      fields: string[];
   }
): Dashboard | null {
   if (!dashboard) return null;

   const layoutConfigurations = Object.keys(dashboardTileLayoutConfigurations)
      .filter((key) => key !== 'dashboardTileId' && key !== 'fields')
      .reduce((obj, key) => {
         obj[key] = dashboardTileLayoutConfigurations[key];
         return obj;
      }, {});

   return {
      ...dashboard,
      dashboardTiles: [
         ...dashboard.dashboardTiles.map((dt) => {
            const newDashboardTileObject = {
               ...dt,
               dashboardTileLayoutConfiguration: {
                  ...dt.dashboardTileLayoutConfiguration,
                  ...layoutConfigurations,
               },
            };
            return dashboardTileLayoutConfigurations.dashboardTileId === dt.dashboardTileId
               ? {
                    ...newDashboardTileObject,
                    viewMode: getTileViewMode(newDashboardTileObject),
                 }
               : dt;
         }),
      ],
   };
}
function getUpdatedSelectedDashboardFromGridLayoutSaveSuccess(
   dashboard: Dashboard | null,
   dirtyDashboardTileLayoutConfigurations: SaveDashboardTileLayoutConfigurations_saveDashboardTileLayoutConfigurations[],
   columnLayoutSize: number
): Dashboard | null {
   if (!dashboard) return null;

   return {
      ...dashboard,
      lastSavedColumnLayoutSize: columnLayoutSize,
      dashboardTiles: [
         ...dashboard.dashboardTiles.map((dt) => {
            const matchingDirtyLayoutConfiguration = dirtyDashboardTileLayoutConfigurations.find(
               (ddtlc) => ddtlc.dashboardTileId === dt.dashboardTileId
            );

            if (matchingDirtyLayoutConfiguration) {
               const newDashboardTileObject = {
                  ...dt,
                  viewMode: matchingDirtyLayoutConfiguration.dashboardTile.tileViewMode ?? TileViewModes.Medium,
                  dashboardTileLayoutConfiguration: {
                     ...dt.dashboardTileLayoutConfiguration,
                     width: matchingDirtyLayoutConfiguration?.width ?? dt.dashboardTileLayoutConfiguration.width,
                     height: matchingDirtyLayoutConfiguration?.height ?? dt.dashboardTileLayoutConfiguration.height,
                     x: matchingDirtyLayoutConfiguration?.column ?? dt.dashboardTileLayoutConfiguration.x,
                     y: matchingDirtyLayoutConfiguration?.row ?? dt.dashboardTileLayoutConfiguration.y,
                  },
               };
               return { ...newDashboardTileObject, viewMode: getTileViewMode(newDashboardTileObject) };
            }
            return dt;
         }),
      ],
   };
}
function getUpdatedSelectedWithDashboardTileUpdateSuccess(
   dashboard: Dashboard | null,
   dashboardTile: DashboardTile
): Dashboard | null {
   if (!dashboard) return null;

   return {
      ...dashboard,
      dashboardTiles: [
         ...dashboard.dashboardTiles.map((dt) => {
            const newDashboardTileObject = {
               ...dt,
               ...dashboardTile,
            };
            return dashboardTile.dashboardTileId === dt.dashboardTileId
               ? {
                    ...newDashboardTileObject,
                    viewMode: getTileViewMode(newDashboardTileObject),
                 }
               : dt;
         }),
      ],
   };
}
function getUpdatedSelectedDashboardFromTileDeleteSuccess(
   dashboard: Dashboard | null,
   dashboardTileIdToRemove: number
): Dashboard | null {
   if (!dashboard) return null;

   return {
      ...dashboard,
      dashboardTiles: [...dashboard.dashboardTiles.filter((dt) => dt.dashboardTileId !== dashboardTileIdToRemove)],
   };
}
